var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, Checkbox, FormHelperText, Link, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { TorqcloudApi } from '../../../api/generated/torqcloud/torqcloudApi';
import useMounted from '../../../hooks/useMounted';
import useAuth from '../../../hooks/useAuth';
import { useAppConfig } from '../../../hooks/useConfig';
import { EULADialog } from './EULADialog';
import { login } from '../../../constants/routing';
import { track } from '../../../analytics/analytics';
import { eventTypes } from '../../../analytics/constants';
const Register = (_a) => {
    var { returnTo } = _a, props = __rest(_a, ["returnTo"]);
    const mounted = useMounted();
    const navigate = useNavigate();
    const [showAdminConfirmMessage, setShowAdminConfirmMessage] = useState(false);
    const { authenticationManager } = useAuth();
    const appConfig = useAppConfig();
    const torqCloudApi = new TorqcloudApi({ baseUrl: appConfig.torqCloudApiUrl });
    const adminConfirmMode = appConfig.cognitoAdminConfirmation;
    const useUuidUsername = appConfig.cognitoUseUuidUsername;
    // EULA state
    const [eulaDialogOpen, setEulaDialogOpen] = useState(false);
    const [eulaContent, setEulaContent] = useState('');
    const [eulaVersion, setEulaVersion] = useState('');
    const [eulaLoading, setEulaLoading] = useState(false);
    const [eulaError, setEulaError] = useState(null);
    // Fetch EULA on mount
    useEffect(() => {
        const fetchEULA = () => __awaiter(void 0, void 0, void 0, function* () {
            setEulaLoading(true);
            setEulaError(null);
            try {
                const response = yield torqCloudApi.v1.v1UsersEulaRetrieve();
                setEulaContent(response.data.content);
                setEulaVersion(response.data.version);
            }
            catch (err) {
                console.error('Failed to fetch EULA:', err);
                setEulaError('Failed to load End User License Agreement. Please try again later.');
            }
            finally {
                setEulaLoading(false);
            }
        });
        fetchEULA();
    }, []);
    const handleRegister = (values_1, _a) => __awaiter(void 0, [values_1, _a], void 0, function* (values, { setErrors, setStatus, setSubmitting }) {
        if (!authenticationManager.accountManager) {
            throw new Error('[auth] missing account manager');
        }
        const email = values.email.toLowerCase();
        try {
            const username = useUuidUsername ? crypto.randomUUID() : email;
            const res = yield authenticationManager.accountManager.signUp(Object.assign({ username, password: values.password }, (useUuidUsername && { attributes: { email } })));
            const userId = useUuidUsername ? username : res.userId;
            yield torqCloudApi.v1.v1UsersRegisterCreate({
                email: email,
                password: values.password,
                user_id: userId,
                eula_version: eulaVersion,
                accept_eula: values.policy,
            });
            if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
            }
            track(eventTypes.USER_SIGNUP, { email, adminConfirmMode, eulaVersion, returnTo });
            if (adminConfirmMode) {
                setShowAdminConfirmMessage(true);
            }
            else {
                navigate('/verify-email', {
                    state: { email, username, password: values.password },
                });
            }
        }
        catch (err) {
            const message = err instanceof Error ? err.message : `${err}`;
            const errorCode = typeof err === 'object' && err != null && 'code' in err ? err.code : null;
            track(eventTypes.SIGNUP_FAILED, { email, errorCode, errorMessage: message });
            if (mounted.current) {
                setStatus({ success: false });
                setErrors({ submit: message });
                setSubmitting(false);
            }
            toast.error(`error ${message}`);
        }
    });
    if (showAdminConfirmMessage) {
        return (_jsxs(Box, { sx: { marginBottom: '2rem' }, children: ["We are now reviewing your account for approval.", returnTo && (_jsxs("span", { children: [' ', "Once approved you can ", _jsx("a", { href: `${login()}?returnTo=${returnTo}`, children: "Login Here." })] }))] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(Formik, { initialValues: {
                    email: '',
                    password: '',
                    policy: false,
                    submit: '',
                }, validationSchema: Yup.object().shape({
                    email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
                    password: Yup.string().min(7).max(255).required('Password is required'),
                    policy: Yup.boolean().oneOf([true], 'This field must be checked'),
                }), onSubmit: handleRegister, children: ({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (_jsxs("form", Object.assign({ noValidate: true, onSubmit: handleSubmit }, props, { children: [_jsxs("div", { className: "form-inputs", children: [_jsx("div", { className: "form-input", children: _jsxs("label", { children: ["Email address", _jsx(TextField, { error: Boolean(touched.email && errors.email), fullWidth: true, helperText: touched.email && errors.email, margin: "dense", name: "email", onBlur: handleBlur, onChange: handleChange, type: "email", value: values.email, variant: "outlined", size: "small" })] }) }), _jsx("div", { className: "form-input", style: { marginTop: '1rem' }, children: _jsxs("label", { children: ["Password", _jsx(TextField, { error: Boolean(touched.password && errors.password), fullWidth: true, helperText: touched.password && errors.password, label: "Password", margin: "dense", name: "password", onBlur: handleBlur, onChange: handleChange, type: "password", value: values.password, variant: "outlined", size: "small" })] }) })] }), _jsxs(Box, { sx: {
                                alignItems: 'center',
                                display: 'flex',
                                ml: -1,
                                mt: 3,
                            }, children: [_jsx(Checkbox, { checked: values.policy, color: "primary", name: "policy", onChange: handleChange }), _jsxs(Typography, { color: "textSecondary", variant: "body2", children: ["I have read the", ' ', _jsx(Link, { color: "primary", component: "button", type: "button", onClick: e => {
                                                e.preventDefault();
                                                setEulaDialogOpen(true);
                                            }, sx: { cursor: 'pointer', textDecoration: 'underline' }, children: "End User License Agreement" })] })] }), Boolean(touched.policy && errors.policy) && _jsx(FormHelperText, { error: true, children: errors.policy }), errors.submit && (_jsx(Box, { sx: { mt: 3 }, children: _jsx(FormHelperText, { error: true, children: errors.submit }) })), _jsx(Box, { sx: { mt: 2 }, children: _jsx(Button, { sx: theme => ({
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'black',
                                    fontWeight: 'bold',
                                    padding: '.4rem 2.45rem',
                                    '&:hover': {
                                        backgroundColor: theme.typography.button.hover,
                                    },
                                }), disabled: isSubmitting, size: "large", type: "submit", variant: "contained", children: "Register" }) })] }))) }), _jsx(EULADialog, { open: eulaDialogOpen, onClose: () => setEulaDialogOpen(false), eulaContent: eulaContent, eulaVersion: eulaVersion, loading: eulaLoading, error: eulaError })] }));
};
export default Register;
//# sourceMappingURL=Register.js.map